import {
  COACH_ACTIONS,
  type CoachActionDefinition,
  type CoachActionId,
  type CoachActionOutcome,
} from './actions';
import { COACH_ANCHORS, type CoachAnchorDefinition, type CoachAnchorId } from './anchors';
import { COACH_EVENTS, type CoachEvent, type CoachEventDefinition } from './events';
import { COACH_FORMS, type CoachFormDefinition, type CoachFormId } from './forms';
import { COACH_SLOTS, type CoachSlotDefinition, type CoachSlotId } from './slots';
import {
  END_STEP,
  type CoachCondition,
  type CoachContextValue,
  type CoachJourney,
  type CoachMedia,
  type CoachSide,
  type CoachStep,
  type CoachWork,
  type CoachChoiceIcon,
} from './schema';

export type CoachContext = Record<string, CoachContextValue>;

/** One user's progress through one journey. Plain JSON so it can be stored. */
export interface CoachRun {
  journeyId: string;
  version: number;
  stepId: string;
  context: CoachContext;
  startedAt: number;
  /** The step whose work (`do`) has begun, so a reload or a re-render never runs it twice. */
  worked?: string;
  /** The step whose work all landed, so a reload there is not taken for an interruption. */
  landed?: string;
  /** Nodes reported finished processing (or failed) during the run, for `waitFor`. */
  settled?: string[];
  /** Nodes added to the board during the run (by the journey or the person), for a restart. */
  created?: string[];
  /** The step whose `when` was checked and held, so it is checked once per visit. */
  checked?: string;
}

export type CoachEndReason = 'completed' | 'dismissed';

/**
 * What moving a run produced: a new step (`run`), the same step with more in its context
 * (`stay`: a form's answer, what its work returned), the end of the journey, or nothing.
 */
export type CoachMove = { run: CoachRun } | { stay: CoachRun } | { end: 'completed' } | null;

/** How many node ids a run remembers in each list: far more than any journey makes or waits on. */
const REMEMBERED_LIMIT = 200;

function remember(run: CoachRun, list: 'settled' | 'created', nodeId: string): CoachRun {
  const ids = run[list] ?? [];
  if (ids.includes(nodeId)) return run;
  return { ...run, [list]: [...ids, nodeId].slice(-REMEMBERED_LIMIT) };
}

/** Moves to the step's `waitFor.to` once every node it waits on (that is bound) has settled. */
export function checkWait(journey: CoachJourney, run: CoachRun): CoachMove {
  const waitFor = getStep(journey, run.stepId)?.waitFor;
  if (!waitFor) return null;
  const settled = new Set(run.settled ?? []);
  const waiting = waitFor.nodes
    .map(key => run.context[key])
    .filter((id): id is string => typeof id === 'string');
  return waiting.every(id => settled.has(id)) ? moveTo(run, waitFor.to, run.context) : null;
}

/** The run when a step's `waitFor.atMost` ran out: on to `waitFor.to`, as if all had finished. */
export function applyTimeout(journey: CoachJourney, run: CoachRun): CoachMove {
  const waitFor = getStep(journey, run.stepId)?.waitFor;
  return waitFor ? moveTo(run, waitFor.to, run.context) : null;
}

export function getStep(journey: CoachJourney, stepId: string): CoachStep | undefined {
  return journey.steps.find(step => step.id === stepId);
}

export function startRun(journey: CoachJourney, context: CoachContext, now: number): CoachRun {
  return {
    journeyId: journey.id,
    version: journey.version,
    stepId: journey.steps[0]!.id,
    context: { ...context },
    startedAt: now,
  };
}

function moveTo(run: CoachRun, to: string, context: CoachContext): CoachMove {
  if (to === END_STEP) return { end: 'completed' };
  // A new step: its work has not begun nor its `when` been checked, even when it is the step
  // the run just left.
  const { worked: _worked, landed: _landed, checked: _checked, ...rest } = run;
  return { run: { ...rest, stepId: to, context } };
}

/**
 * The run as a step begins: a form there asks afresh (an answer kept from an earlier visit
 * would otherwise run the step's work at once).
 */
export function beginStep(journey: CoachJourney, run: CoachRun): CoachRun {
  const bind = getStep(journey, run.stepId)?.form?.bind;
  if (!bind || run.context[bind] === undefined) return run;
  const { [bind]: _answer, ...context } = run.context;
  return { ...run, context };
}

/** Whether every fact a condition names holds; a fact the app has not reported is unknown. */
function conditionState(
  when: CoachCondition | undefined,
  facts: CoachCondition
): 'holds' | 'fails' | 'unknown' {
  if (!when) return 'holds';
  let state: 'holds' | 'fails' | 'unknown' = 'holds';
  for (const [key, value] of Object.entries(when) as [keyof CoachCondition, string][]) {
    const fact = facts[key];
    if (fact === undefined) state = state === 'fails' ? 'fails' : 'unknown';
    else if (fact !== value) state = 'fails';
  }
  return state;
}

/**
 * Checks a step's `when` once, when the facts it needs are known: holding, the step goes on
 * (and is marked checked); not holding, the run moves to `otherwise`. Null while unknown.
 */
export function checkWhen(journey: CoachJourney, run: CoachRun, facts: CoachCondition): CoachMove {
  const step = getStep(journey, run.stepId);
  if (!step?.when || !step.otherwise || run.checked === step.id) return null;
  const state = conditionState(step.when, facts);
  if (state === 'unknown') return null;
  if (state === 'fails') return moveTo(run, step.otherwise, run.context);
  return { stay: { ...run, checked: step.id } };
}

/** Whether a step is still waiting to learn if it happens at all (its `when` is unchecked). */
function awaitingWhen(step: CoachStep, run: CoachRun): boolean {
  return Boolean(step.when) && run.checked !== step.id;
}

/** The run after `event`, if the current step listens for it. `fresh` comes from the store. */
export function applyEvent(
  journey: CoachJourney,
  run: CoachRun,
  event: CoachEvent,
  fresh: boolean
): CoachMove {
  const step = getStep(journey, run.stepId);
  if (!step) return null;
  const payload = event.payload as Record<string, CoachContextValue>;
  const nodeId = typeof payload.nodeId === 'string' ? payload.nodeId : null;
  // Remembered whatever the step: what finished (or failed), for `waitFor`; what was added,
  // for a restart to take away.
  let current = run;
  if (nodeId && (event.name === 'node.ready' || event.name === 'node.failed'))
    current = remember(current, 'settled', nodeId);
  if (nodeId && event.name === 'node.added') current = remember(current, 'created', nodeId);
  const answer = contentAnswer(step, current, event, fresh);
  if (answer) return answer;
  const move = transitionFor(step, current, event.name, payload, fresh);
  if (move) return move;
  if (current === run) return null;
  return checkWait(journey, current) ?? { stay: current };
}

/**
 * A `content` form's answer: the first node of a type it accepts that is added while its card
 * is up (`fresh`: not something already there when the step began).
 */
function contentAnswer(
  step: CoachStep,
  run: CoachRun,
  event: CoachEvent,
  fresh: boolean
): CoachMove {
  if (event.name !== 'node.added' || !fresh || awaitingWhen(step, run)) return null;
  const form = step.form;
  if (!form?.accept || run.context[form.bind] !== undefined) return null;
  const { nodeId, nodeType } = event.payload;
  if (!(form.accept as readonly string[]).includes(nodeType)) return null;
  return answerForm(step, run, nodeId);
}

function answerForm(step: CoachStep, run: CoachRun, value: string): CoachMove {
  const form = step.form!;
  const context = { ...run.context, [form.bind]: value };
  // An answer is a new attempt at the step's work (after one that failed, say).
  const { worked: _worked, landed: _landed, ...rest } = run;
  if (step.do) return { stay: { ...rest, context } };
  return moveTo(run, form.to ?? END_STEP, context);
}

function transitionFor(
  step: CoachStep,
  run: CoachRun,
  name: CoachEvent['name'],
  payload: Record<string, CoachContextValue>,
  fresh: boolean
): CoachMove {
  const event = { name };
  for (const transition of step.on) {
    if (transition.event !== event.name) continue;
    if (transition.fresh && !fresh) continue;
    const matches = transition.match.every(
      key => run.context[key] === undefined || run.context[key] === payload[key]
    );
    if (!matches) continue;
    const matchesAs = Object.entries(transition.matchAs ?? {}).every(
      ([field, key]) => run.context[key] !== undefined && run.context[key] === payload[field]
    );
    if (!matchesAs) continue;
    const conditionsHold = Object.entries(transition.if ?? {}).every(([key, value]) =>
      Array.isArray(value) ? value.includes(String(payload[key])) : payload[key] === value
    );
    if (!conditionsHold) continue;
    const context = { ...run.context };
    for (const key of transition.bind) {
      if (payload[key] !== undefined) context[key] = payload[key];
    }
    for (const [field, key] of Object.entries(transition.bindAs ?? {})) {
      if (payload[field] !== undefined) context[key] = payload[field];
    }
    return moveTo(run, transition.to, context);
  }
  return null;
}

/** The run after the person picks one of the card's choices (its index). */
export function applyChoice(journey: CoachJourney, run: CoachRun, index: number): CoachMove {
  const choice = getStep(journey, run.stepId)?.choices?.[index];
  if (!choice) return null;
  return moveTo(run, choice.to, { ...run.context, ...choice.set });
}

/** The run after the user presses the current card's button. */
export function applyAction(journey: CoachJourney, run: CoachRun): CoachMove {
  const action = getStep(journey, run.stepId)?.action;
  if (!action) return null;
  const context = { ...run.context };
  for (const key of action.unbind) delete context[key];
  return moveTo(run, action.to, context);
}

/** A run restored after a page load, moved to its step's `resumeAt` if it has one. */
export function resumeRun(journey: CoachJourney, run: CoachRun): CoachRun | null {
  const step = getStep(journey, run.stepId);
  if (!step) return null;
  if (!step.resumeAt) return run;
  const { worked: _worked, landed: _landed, ...rest } = run;
  return { ...rest, stepId: step.resumeAt };
}

/**
 * The work the current step should begin now: a step's `do` runs once per visit, when the
 * step begins, or for a step with a form once the answer is in. Nothing while the step is
 * still learning whether it happens (`when`).
 */
export function pendingWork(journey: CoachJourney, run: CoachRun): CoachWork[] | null {
  const step = getStep(journey, run.stepId);
  if (!step?.do || run.worked === step.id || awaitingWhen(step, run)) return null;
  if (step.form && run.context[step.form.bind] === undefined) return null;
  return step.do;
}

/** The run with its current step's work marked as begun. */
export function markWorked(run: CoachRun): CoachRun {
  return { ...run, worked: run.stepId };
}

const TEXT_SLOT = /\{\{([a-zA-Z0-9]+)\}\}/g;

/** Text with each `{{key}}` replaced by its context value, or the keys it needs and lacks. */
export function fillText(
  text: string,
  context: CoachContext
): { ok: true; text: string } | { ok: false; missing: string[] } {
  const missing: string[] = [];
  const filled = text.replace(TEXT_SLOT, (_, key: string) => {
    const value = context[key];
    if (typeof value === 'string') return value;
    missing.push(key);
    return '';
  });
  return missing.length ? { ok: false, missing } : { ok: true, text: filled };
}

/**
 * What the app is asked to do for a step's work: the action and its params, with every ref
 * read from context and every `{{key}}` filled in. Fails, naming what is missing, when the
 * context lacks a single ref or a text slot it needs; a list of refs keeps only those set.
 */
export function resolveWork(
  work: CoachWork,
  context: CoachContext
):
  | { ok: true; action: CoachActionId; input: Record<string, unknown> }
  | { ok: false; error: string } {
  const definition: CoachActionDefinition = COACH_ACTIONS[work.action];
  const parsed = definition.params.safeParse(work.params);
  if (!parsed.success) return { ok: false, error: `"${work.action}" got params it cannot use` };
  const params = parsed.data as Record<string, unknown>;
  const missing = new Set<string>();
  const read = (key: string): string | undefined => {
    const value = context[key];
    if (typeof value === 'string') return value;
    missing.add(key);
    return undefined;
  };
  const input: Record<string, unknown> = {};
  for (const [field, value] of Object.entries(params)) {
    if (definition.refs.includes(field)) {
      // A list takes what is there: an answer the person skipped is simply not in it.
      input[field] = Array.isArray(value)
        ? value.flatMap(key => {
            const found = context[String(key)];
            return typeof found === 'string' ? [found] : [];
          })
        : read(String(value));
    } else if (typeof value === 'string') {
      const filled = fillText(value, context);
      if (filled.ok) input[field] = filled.text;
      else filled.missing.forEach(key => missing.add(key));
    } else if (
      (work.action === 'copilot.ask' || work.action === 'copilot.fill') &&
      field === 'context' &&
      value &&
      typeof value === 'object'
    ) {
      // Task fields are strings or literals; interpolate data without exposing it in chat.
      input[field] = Object.fromEntries(
        Object.entries(value).map(([key, item]) => {
          if (typeof item !== 'string') return [key, item];
          const filled = fillText(item, context);
          if (!filled.ok) {
            filled.missing.forEach(key => missing.add(key));
            return [key, ''];
          }
          return [key, filled.text];
        })
      );
    } else {
      input[field] = value;
    }
  }
  if (missing.size > 0) {
    return { ok: false, error: `"${work.action}" needs ${[...missing].join(', ')}` };
  }
  return { ok: true, action: work.action, input };
}

/**
 * The run after one of its step's works (`index` in the list) finished. Ignored when the run
 * has moved on meanwhile (an event arrived first). What it returned is kept under its `bind`.
 * A failure goes to its `onError`, or stays (the card offers Try again); the last success goes
 * to its `then`, or stays to wait for the step's events. Any other success stays, for the
 * store to do the next one.
 */
export function applyWorkOutcome(
  journey: CoachJourney,
  run: CoachRun,
  stepId: string,
  index: number,
  outcome: CoachActionOutcome
): CoachMove {
  if (run.stepId !== stepId) return null;
  const works = getStep(journey, stepId)?.do;
  const work = works?.[index];
  if (!works || !work) return null;
  if (!outcome.ok) return work.onError ? moveTo(run, work.onError, run.context) : null;
  const context = { ...run.context };
  const result = outcome.result as Record<string, string | undefined>;
  for (const [returned, key] of Object.entries(work.bind)) {
    const value = result[returned];
    if (typeof value === 'string') context[key] = value;
  }
  const last = index === works.length - 1;
  return last && work.then ? moveTo(run, work.then, context) : { stay: { ...run, context } };
}

/**
 * The run after the person answers the current card's form (the words typed; a `content`
 * form answers itself from what is added). A step with work stays, and the work begins with
 * the answer; one without moves to the form's `to`.
 */
export function applyForm(journey: CoachJourney, run: CoachRun, value: string): CoachMove {
  const step = getStep(journey, run.stepId);
  if (!step?.form || awaitingWhen(step, run)) return null;
  return answerForm(step, run, value);
}

/** What a card points at: an anchor in the product, or a node on the board. */
export type CoachTarget =
  | {
      anchor: CoachAnchorId;
      /** Instance id when the anchor repeats and the journey has bound one. */
      scope?: string;
    }
  | { node: string };

/** What to render right now. */
export interface CoachView {
  stepId: string;
  display: 'card' | 'toast' | 'inline' | 'conversation';
  slot: CoachSlotId | null;
  title: string;
  body: string;
  target: CoachTarget | null;
  progress: { chapter: number; total: number } | null;
  action: string | null;
  /** Where the step's button goes: on the card, or under what the card points at. */
  actionPlacement: 'card' | 'target';
  form: CoachFormView | null;
  media: CoachMedia | null;
  /** The card's choice buttons, by label; `applyChoice` takes the index. */
  choices: string[];
  /** Each choice's logos, in the same order as `choices`. */
  choiceIcons: CoachChoiceIcon[][];
  /** The side the card sits on, when the step says; otherwise the side with room. */
  placement: CoachSide | null;
  /** Whether the rest of the screen dims around the target. */
  spotlight: boolean;
  /** For a card pointing at nothing: floating in a corner, or centred over the dimmed board. */
  stage: 'float' | 'board';
}

/** A form as the card draws it. */
export interface CoachFormView {
  id: CoachFormId;
  label: string;
  placeholder: string;
  multiline: boolean;
  answers: 'text' | 'node';
  /** The typed answer's button. */
  submit: string | null;
  /** For `content`: the node types offered, first ones first. */
  accept: readonly string[];
  /** For a typed answer: ready answers that fill the box when tapped. */
  suggestions: readonly { label: string; text: string }[];
}

function formView(step: CoachStep): CoachFormView | null {
  if (!step.form) return null;
  const definition: CoachFormDefinition = COACH_FORMS[step.form.id];
  return {
    id: step.form.id,
    label: definition.label,
    placeholder: step.form.placeholder ?? definition.placeholder,
    multiline: definition.multiline ?? false,
    answers: definition.answers,
    submit: step.form.submit ?? null,
    accept: step.form.accept ?? [],
    suggestions: step.form.suggestions ?? [],
  };
}

export function targetFor(anchor: CoachAnchorId, context: CoachContext, key?: string): CoachTarget {
  const definition: CoachAnchorDefinition = COACH_ANCHORS[anchor];
  const scopeKey = definition.scope ? (key ?? definition.scope) : undefined;
  const scope = scopeKey ? context[scopeKey] : undefined;
  return typeof scope === 'string' ? { anchor, scope } : { anchor };
}

function conditionHolds(when: CoachCondition | undefined, facts: CoachCondition): boolean {
  return conditionState(when, facts) === 'holds';
}

/**
 * Picks how the current step shows: the first presentation whose condition
 * holds and whose anchor is on screen. With none, the step's own copy shows as
 * a floating card. `null` for hidden steps.
 */
export function presentStep(
  journey: CoachJourney,
  run: CoachRun,
  facts: CoachCondition,
  isOnScreen: (target: CoachTarget) => boolean
): CoachView | null {
  const step = getStep(journey, run.stepId);
  // A step still learning whether it happens shows nothing yet.
  if (!step || step.display === 'hidden' || awaitingWhen(step, run)) return null;
  const copy = (text: string) =>
    text
      .replace(TEXT_SLOT, (_, key: string) => {
        const value = run.context[key];
        return typeof value === 'string' ? value : '';
      })
      .trim();
  const total = Math.max(0, ...journey.steps.map(candidate => candidate.chapter ?? 0));
  const base = {
    stepId: step.id,
    display: step.display,
    slot: step.slot ?? null,
    progress: step.chapter ? { chapter: step.chapter, total } : null,
    action: step.action?.label ?? null,
    actionPlacement: step.action?.placement ?? 'card',
    form: formView(step),
    choices: step.choices?.map(choice => choice.label) ?? [],
    choiceIcons: step.choices?.map(choice => choice.icons) ?? [],
    stage: step.stage,
  };
  for (const presentation of step.show) {
    if (!conditionHolds(presentation.when, facts)) continue;
    const nodeId = presentation.node ? run.context[presentation.node] : undefined;
    // A node the journey has not bound yet cannot be pointed at: try the next way to show.
    if (presentation.node && typeof nodeId !== 'string') continue;
    const target: CoachTarget | null = presentation.anchor
      ? targetFor(presentation.anchor, run.context, presentation.in)
      : typeof nodeId === 'string'
        ? { node: nodeId }
        : null;
    if (target && !isOnScreen(target)) continue;
    return {
      ...base,
      title: copy(presentation.title ?? step.title),
      body: copy(presentation.body ?? step.body),
      media: presentation.media ?? step.media ?? null,
      placement: presentation.placement ?? null,
      spotlight: presentation.spotlight,
      target,
    };
  }
  return {
    ...base,
    title: copy(step.title),
    body: copy(step.body),
    media: step.media ?? null,
    placement: null,
    spotlight: true,
    target: null,
  };
}

/** How a step frames its node: room for the card on a side, and space around it. */
export interface CoachFraming {
  placement: CoachSide | null;
  padding: number | null;
  maxZoom: number | null;
}

/** The canvas node to keep in view for the current step (or a type to find one by), and how. */
export function focusFor(
  journey: CoachJourney,
  run: CoachRun
): (({ nodeId: string } | { nodeType: string }) & CoachFraming) | null {
  const focus = getStep(journey, run.stepId)?.focus;
  if (!focus) return null;
  const framing = {
    placement: focus.placement ?? null,
    padding: focus.padding ?? null,
    maxZoom: focus.maxZoom ?? null,
  };
  const nodeId = run.context[focus.node];
  if (typeof nodeId === 'string') return { nodeId, ...framing };
  return focus.orFirst ? { nodeType: focus.orFirst, ...framing } : null;
}

/** The value an event is about, for freshness checks (`chat.script-ready` → its toolCallId). */
export function eventSubject(event: CoachEvent): string | null {
  const definition: CoachEventDefinition = COACH_EVENTS[event.name];
  if (!definition.id) return null;
  const value = (event.payload as Record<string, CoachContextValue>)[definition.id];
  return typeof value === 'string' ? `${event.name}:${value}` : null;
}

/**
 * The view for a product slot: the current step's copy when it shows inline in
 * `slot`, for the instance the journey is about (`scope`, e.g. a project id).
 */
export function presentSlot(
  journey: CoachJourney,
  run: CoachRun,
  slot: CoachSlotId,
  scope?: string
): CoachView | null {
  const view = presentStep(journey, run, {}, () => false);
  if (!view || view.display !== 'inline' || view.slot !== slot) return null;
  const definition: CoachSlotDefinition = COACH_SLOTS[slot];
  if (definition.scope && run.context[definition.scope] !== scope) return null;
  return view;
}
